import React from 'react';
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { WorkoutStatsCompact } from '../WorkoutStatsCompact';
import { useLanguage } from '../../../i18n/LanguageContext';

interface WorkoutStatsSectionProps {
  onNavigateToWrapped: () => void;
  onServiceUnavailable?: () => void;
  statsExpanded: boolean;
  setStatsExpanded: (expanded: boolean) => void;
}

export const WorkoutStatsSection: React.FC<WorkoutStatsSectionProps> = ({
  onNavigateToWrapped,
  onServiceUnavailable,
  statsExpanded,
  setStatsExpanded,
}) => {
  const { t } = useLanguage();

  return (
    <>
      {/* 2025 Wrapped Button (Mobile) */}
      <Paper
        elevation={3}
        sx={{
          mt: 2,
          mb: 2,
          background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          },
          display: { xs: 'block', md: 'none' } // Only show on mobile
        }}
        onClick={onNavigateToWrapped}
      >
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EmojiEventsIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              >
                {t('wrapped.navTitle') || '2025 Wrapped'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.875rem',
                }}
              >
                {t('wrapped.subtitle') || 'Your Year of Gains'}
              </Typography>
            </Box>
          </Box>
          <AutoAwesomeIcon
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: 32,
            }}
          />
        </Box>
      </Paper>

      {/* Collapsible Workout Statistics */}
      <Accordion
        expanded={statsExpanded}
        onChange={(e, isExpanded) => setStatsExpanded(isExpanded)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t('workoutStats.title')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <WorkoutStatsCompact onServiceUnavailable={onServiceUnavailable} hideHeader={true} noCard={true} />
        </AccordionDetails>
      </Accordion>
    </>
  );
};
